import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import axios from '../../Config/axiosConfig';
import { arrayBufferToDataUrl, normalizeSignatureImage } from './signatureImage';
import './SignatureEditor.css';

const DEFAULT_SIZE = 100;
const NORMALIZED_IMAGE_SIZE = 100;

const getStoredSettings = (path = '') => {
    const match = path.match(/_escala-(\d{2,3})_(livre|limite)/i);
    return {
        size: match ? Math.min(Math.max(Number(match[1]), 60), 200) : DEFAULT_SIZE,
        allowOverflow: match?.[2]?.toLowerCase() === 'livre',
    };
};

const removeStoredSettings = (name = 'assinatura') => name
    .replace(/\.[^.]+$/, '')
    .replace(/_ajustada$/i, '')
    .replace(/(?:^|_)escala-\d{2,3}_(livre|limite)_?/gi, '');

const SignatureEditor = ({ api, currentImagePath, onChange, label }) => {
    const [source, setSource] = useState('');
    const [fileName, setFileName] = useState('assinatura.png');
    const [preview, setPreview] = useState('');
    const [size, setSize] = useState(DEFAULT_SIZE);
    const [allowOverflow, setAllowOverflow] = useState(false);
    const [error, setError] = useState('');
    const changeSequence = useRef(0);

    useEffect(() => {
        setSource('');
        setPreview('');
        const storedSettings = getStoredSettings(currentImagePath);
        setSize(storedSettings.size);
        setAllowOverflow(storedSettings.allowOverflow);
        setError('');

        if (!currentImagePath || !api) {
            onChange(null);
            return undefined;
        }

        let active = true;
        axios
            .post(api, { path: currentImagePath }, { responseType: 'arraybuffer' })
            .then((response) => {
                if (!active) return;
                const contentType = response.headers['content-type'] || 'image/png';
                setFileName(currentImagePath.split(/[\\/]/).pop() || 'assinatura.png');
                setSource(arrayBufferToDataUrl(response.data, contentType));
            })
            .catch(() => {
                if (active) setError('Não foi possível carregar a assinatura atual. Selecione uma nova imagem.');
            });

        return () => {
            active = false;
        };
    }, [api, currentImagePath, onChange]);

    useEffect(() => {
        if (!source) return undefined;

        const sequence = changeSequence.current + 1;
        changeSequence.current = sequence;
        let active = true;

        normalizeSignatureImage(source, NORMALIZED_IMAGE_SIZE)
            .then(({ blob, dataUrl }) => {
                if (!active || sequence !== changeSequence.current) return;
                const overflowSetting = allowOverflow ? 'livre' : 'limite';
                const normalizedName = `escala-${size}_${overflowSetting}_${removeStoredSettings(fileName)}.png`;
                setPreview(dataUrl);
                setError('');
                onChange(new File([blob], normalizedName, { type: 'image/png' }));
            })
            .catch(() => {
                if (!active || sequence !== changeSequence.current) return;
                setPreview('');
                onChange(null);
                setError('Formato de imagem inválido. Use PNG ou JPG.');
            });

        return () => {
            active = false;
        };
    }, [allowOverflow, fileName, onChange, size, source]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            setSource('');
            setPreview('');
            onChange(null);
            return;
        }

        if (!file.type.startsWith('image/')) {
            setError('Selecione uma imagem PNG ou JPG.');
            event.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setFileName(file.name);
            setSize(DEFAULT_SIZE);
            setAllowOverflow(false);
            setSource(reader.result);
        };
        reader.onerror = () => setError('Não foi possível ler o arquivo selecionado.');
        reader.readAsDataURL(file);
    };

    return (
        <Form.Group controlId={`assinatura-${label}`} className="mt-3">
            <Form.Label className="bold-label">*{label}:</Form.Label>
            <Form.Control type="file" accept="image/png,image/jpeg" onChange={handleFileChange} />

            {preview && (
                <div className="signature-editor">
                    <div className="signature-editor__preview-stage">
                        <div
                            className={`signature-editor__preview ${allowOverflow ? 'signature-editor__preview--overflow' : ''}`}
                            aria-label="Prévia da assinatura no laudo"
                        >
                            <img
                                src={preview}
                                alt="Prévia da assinatura no laudo"
                                style={{ transform: `translate(-50%, -50%) scale(${size / 100})` }}
                            />
                        </div>
                    </div>
                    <div className="signature-editor__controls">
                        <Form.Label htmlFor={`tamanho-${label}`}>
                            Tamanho no laudo: {size}%
                        </Form.Label>
                        <Form.Range
                            id={`tamanho-${label}`}
                            min="60"
                            max="200"
                            step="1"
                            value={size}
                            onChange={(event) => setSize(Number(event.target.value))}
                        />
                        <Form.Check
                            id={`permitir-exceder-${label}`}
                            type="switch"
                            checked={allowOverflow}
                            onChange={(event) => setAllowOverflow(event.target.checked)}
                            label="Permitir que a assinatura ultrapasse as linhas"
                            className="mt-2"
                        />
                        <small className="text-muted">
                            A moldura representa o espaço disponível no PDF. Com a opção desativada, o excesso será cortado na linha.
                        </small>
                    </div>
                </div>
            )}

            {error && <div className="text-danger mt-2">{error}</div>}
        </Form.Group>
    );
};

export default SignatureEditor;

